// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useRef, useEffect } from 'react';
import * as NavUtils from '../../utils/game/NavigationUtils';
import * as WarpCalc from '../../utils/game/WarpCalculations';
import * as WarpTravelMgr from '../../utils/game/WarpTravelManager';
import GameAlert from '../../utils/ui/GameAlertUtils';
import EventBus, { EVENT_TYPES } from '../../utils/system/EventBus';
import SoundEffects from '../../utils/system/SoundEffects';
import GameConfig from '../../config/GameConfig';

/**
 * Custom hook for managing warp travel logic
 * Extracted from NavigationPanel to reduce bundle size
 */
export const useWarpTravelLogic = ({
  selectedSystem,
  currentLocation,
  distancePositions,
  selectedWarpFactor,
  getDefaultWarpFactor,
  getFuelEfficiencyBonus,
  antimatterFuel,
  maxFuel,
  starSystems,
  navigationMode,
  fuelRef,
  warpPausedRef,
  setCurrentLocation,
  setHasArrived,
  setShowArrivalScreen,
  setShowWarpEffect,
  setWarpDisengaging,
  setWarpElements,
  setWarpTravelTime,
  setWarpTravelProgress,
  setFuelDepleted,
  setFuelConsumptionRate,
  setActiveEvent,
  removeFuel,
  enrichLocationWithCoordinates
}) => {
  const warpTimerRef = useRef(null);
  const warpOriginRef = useRef(null);
  const pauseStartTimeRef = useRef(null);
  const warpTravelProgressRef = useRef(0);

  // Calculate distance between systems
  const calculateDistanceBetweenSystems = (fromSystem, toSystem) => {
    if (!fromSystem || !toSystem) return 0;
    return NavUtils.calculateDistance(fromSystem, toSystem, distancePositions);
  };

  // Get display distance to selected system
  const getDisplayDistance = (system) => {
    if (!system || !currentLocation) return 0;
    return NavUtils.calculateDistance(currentLocation, system, distancePositions);
  };

  // Generate planet stars for arrival animation
  const generatePlanetStars = () => NavUtils.generatePlanetStars(200);

  // Continue warp travel with fuel consumption
  const continueWarpTravel = (travelTime, fuelConsumptionRate) => {
    
    // Get current warp factor for alerts and damage calculations
    const currentWarpFactor = selectedWarpFactor || getDefaultWarpFactor();
    
    // Dispatch warp engaged alert
    GameAlert.info(`🚀 WARP ${currentWarpFactor} ENGAGED - Destination: ${selectedSystem?.name || 'Unknown'}`);
    
    setWarpTravelTime(travelTime);
    setWarpTravelProgress(0);
    setHasArrived(false);
    setFuelDepleted(false);
    setFuelConsumptionRate(fuelConsumptionRate);

    const startTime = Date.now();
    let pausedTime = 0; // Track total time spent paused
    let engineDamageChecked = false; // Track if we've checked for engine damage
    let approachingAnnouncementPlayed = false; // Track if we've played approaching_destination
    
    warpTimerRef.current = setInterval(() => {
      // Check if warp is paused
      if (warpPausedRef && warpPausedRef.current) {
        // Track when pause started
        if (!pauseStartTimeRef.current) {
          pauseStartTimeRef.current = Date.now();
        }
        return; // Skip this interval while paused
      }
      
      // If we were paused and now resumed, add the paused time
      if (pauseStartTimeRef.current) {
        pausedTime += Date.now() - pauseStartTimeRef.current;
        pauseStartTimeRef.current = null;
      }
      
      const now = Date.now();
      const elapsed = now - startTime - pausedTime; // Subtract paused time
      const progress = Math.min(100, (elapsed / (travelTime * 1000)) * 100);
      
      setWarpTravelProgress(progress);
      warpTravelProgressRef.current = progress;
      
      // Check for engine damage at Warp 8 or 9 using utility
      if (!engineDamageChecked && (currentWarpFactor >= 8)) {
        const damageChance = currentWarpFactor === 8 ? 0.15 : 0.25; // 15% at Warp 8, 25% at Warp 9
        if (progress > 30 && Math.random() < damageChance) {
          engineDamageChecked = true;
          // Engine damage would be handled by the enhanced event system (10-25% damage)
        }
      }
      
      // Play approaching_destination announcement when ~95% complete (5 seconds before arrival)
      if (!approachingAnnouncementPlayed && progress >= 95) {
        approachingAnnouncementPlayed = true;
        EventBus.emit(EVENT_TYPES.APPROACHING_DESTINATION);
      }
      
      // Consume fuel during travel and check for depletion
      const fuelConsumed = fuelConsumptionRate * 0.1; // 100ms interval = 0.1 seconds
      removeFuel(fuelConsumed);
      
      // Use ref to get current fuel value (not stale closure value)
      const currentFuel = fuelRef.current - fuelConsumed;
      
      // ===== CREW RELATIONSHIP EVENTS DURING TRAVEL =====
      // Trigger random crew events based on config
      const eventInterval = GameConfig.crewRelationships?.randomEvents?.checkIntervalSeconds || 10;
      const eventChance = GameConfig.crewRelationships?.randomEvents?.triggerChance || 0.1;
      if (Math.floor(progress) % eventInterval === 0 && Math.random() < eventChance) {
        if (window.CrewRelationshipManager && window.CrewManager) {
          const crew = window.CrewManager.getAllCrew();
          const result = window.CrewRelationshipManager.triggerRandomEvent(crew);
          
          if (result.occurred) {
            GameAlert.info(`👥 ${result.message}`);
          }
        }
      }
      // ===== END CREW RELATIONSHIP EVENTS =====
      
      // ===== CENTRALIZED POSITION MANAGEMENT =====
      // Update current location coordinates during warp for real-time position tracking
      if (progress > 0 && progress < 100) {
        const progressPercent = progress / 100;
        const fromPos = distancePositions[currentLocation.id];
        const toPos = distancePositions[selectedSystem.id];
        
        if (fromPos && toPos) {
          const warpPosition = {
            x: fromPos.x + (toPos.x - fromPos.x) * progressPercent,
            y: fromPos.y + (toPos.y - fromPos.y) * progressPercent,
            z: (fromPos.z || 0) + ((toPos.z || 0) - (fromPos.z || 0)) * progressPercent
          };
          
          setCurrentLocation(prev => ({
            ...prev,
            x: warpPosition.x,
            y: warpPosition.y,
            z: warpPosition.z,
            inTransit: true
          }));
        }
      }
      // ===== END CENTRALIZED POSITION MANAGEMENT =====
        
      // Check if fuel depleted - let EnhancedWarpEventManager handle tow offers
      if ((currentFuel <= 0 && !fuelRef.current) || fuelRef.current <= 0) {
        
        GameAlert.critical('⚠️ FUEL DEPLETED - Emergency warp disengage initiated');
        
        setFuelDepleted(true);
        
        // Stop warp travel
        clearInterval(warpTimerRef.current);
        
        // Calculate stranded position for display
        const progressPercent = progress / 100;
        const fromPos = distancePositions[currentLocation.id];
        const toPos = distancePositions[selectedSystem.id];
        
        if (fromPos && toPos) {
          const distanceToDestination = getDisplayDistance(selectedSystem);
          const distanceTraveled = Math.floor(distanceToDestination * progressPercent);
          
          const strandedLocation = {
            id: -1,
            name: `DEEP SPACE (${distanceTraveled.toFixed(2)} LY from ${currentLocation.name})`,
            threat: 'UNKNOWN',
            resources: 'NONE',
            x: fromPos.x + (toPos.x - fromPos.x) * progressPercent,
            y: fromPos.y + (toPos.y - fromPos.y) * progressPercent,
            z: (fromPos.z || 0) + ((toPos.z || 0) - (fromPos.z || 0)) * progressPercent,
            inTransit: false
          };
          
          setCurrentLocation(strandedLocation);
        }
        
        // Stop warp effect after a moment
        setTimeout(() => {
          setShowWarpEffect(false);
          setWarpDisengaging(false);
          setWarpElements([]);
          setWarpTravelProgress(0);
          SoundEffects.stop('warp_travel'); // Stop warp sound
        }, 2000);
        
        return;
      }

      // Auto-arrive when progress reaches 100%
      if (progress >= 100) {
        clearInterval(warpTimerRef.current);
        
        // Dispatch arrival alert
        GameAlert.success(`✓ ARRIVED AT ${selectedSystem?.name || 'DESTINATION'} - Warp travel complete`);
        
        // Clear any active events to prevent them from triggering after arrival
        setActiveEvent(null);
        
        setHasArrived(true);
        setShowArrivalScreen(true); // Show arrival screen with zoom animation
        
        // CRITICAL: Stop warp effect and events immediately when arriving
        setShowWarpEffect(false);
        setWarpDisengaging(false);
        setWarpElements([]);
        SoundEffects.stop('warp_travel'); // Stop warp sound        
        // Update location to destination and clear in-transit coordinates
        setCurrentLocation(prev => {
          const destination = selectedSystem;
          return {
            ...destination,
            x: undefined,
            y: undefined,
            z: undefined,
            inTransit: false
          };
        });
        
        // Don't dispatch planetArrival here - wait for user to click "Enter Orbit"
      }
    }, 100); // Update every 100ms for smooth progress
  };

  // Start warp timer with fuel checks
  const startWarpTimer = (travelTime, isRisky = false) => {
    
    // Clear any existing timer
    if (warpTimerRef.current) {
      clearInterval(warpTimerRef.current);
    }

    // Store origin for distance calculations
    warpOriginRef.current = currentLocation;

    // If risky warp is requested, bypass fuel checks
    if (isRisky) {
      const distance = getDisplayDistance(selectedSystem);
      const currentWarp = selectedWarpFactor || getDefaultWarpFactor();
      const fuelNeeded = NavUtils.calculateFuelNeeded(distance, currentWarp);
      const fuelConsumptionRate = fuelNeeded / travelTime;
      
      setWarpTravelTime(travelTime);
      setWarpTravelProgress(0);
      setHasArrived(false);
      setFuelDepleted(false);
      setFuelConsumptionRate(fuelConsumptionRate);
      
      continueWarpTravel(travelTime, fuelConsumptionRate);
      return;
    }

    // Prepare parameters for WarpTravelManager
    const params = {
      travelTime,
      originLocation: warpOriginRef.current,
      currentLocation,
      selectedSystem,
      distancePositions,
      selectedWarpFactor,
      getDefaultWarpFactor,
      getFuelEfficiencyBonus,
      currentFuel: fuelRef.current,
      maxFuel,
      starSystems,
      calculateDistanceBetweenSystems,
      navigationMode
    };

    // Call WarpTravelManager to check fuel and get result
    const result = WarpTravelMgr.startWarpTimer(params);

    if (result.type === 'INSUFFICIENT_FUEL') {
      // Build actions for insufficient fuel warning
      const actions = [
        {
          label: 'ABORT TRAVEL',
          effect: () => {
            setActiveEvent(null);
            setWarpTravelTime(0);
            setWarpTravelProgress(0);
            setHasArrived(false);
            setShowWarpEffect(false);
            setWarpDisengaging(false);
            setWarpElements([]);
            if (warpTimerRef.current) {
              clearInterval(warpTimerRef.current);
            }
          }
        }
      ];

      // Add "Use Recommended Warp" button if available
      if (result.recommendedWarp) {
        const recommendedFuelNeeded = NavUtils.calculateFuelNeeded(
          result.distance,
          result.recommendedWarp,
          result.upgradeBonuses.fuelEfficiency
        );

        if (recommendedFuelNeeded <= fuelRef.current) {
          actions.unshift({
            label: `USE WARP ${result.recommendedWarp}`,
            effect: () => {
              setActiveEvent(null);
              // Continue with recommended warp factor
              const recommendedTravelTime = WarpCalc.calculateTravelTime(result.distance, result.recommendedWarp);
              const recommendedFuelRate = recommendedFuelNeeded / recommendedTravelTime;
              
              setWarpTravelTime(recommendedTravelTime);
              setWarpTravelProgress(0);
              setHasArrived(false);
              setFuelDepleted(false);
              setFuelConsumptionRate(recommendedFuelRate);
              
              continueWarpTravel(recommendedTravelTime, recommendedFuelRate);
            }
          });
        }
      }

      // Show insufficient fuel warning
      setActiveEvent({
        type: 'WARNING',
        title: result.title,
        message: result.message,
        color: result.color,
        icon: result.icon,
        actions: actions
      });
      return;
    }

    if (result.type === 'START_TRAVEL') {
      // Start normal warp travel
      setWarpTravelTime(result.travelTime);
      setWarpTravelProgress(0);
      setHasArrived(false);
      setFuelDepleted(false);
      setFuelConsumptionRate(result.fuelConsumptionRate);
      
      continueWarpTravel(result.travelTime, result.fuelConsumptionRate);
    }
  };

  // Skip remaining warp travel — consume remaining fuel and arrive instantly
  const skipWarp = () => {
    if (!warpTimerRef.current) return;
    clearInterval(warpTimerRef.current);
    warpTimerRef.current = null;

    // Consume remaining fuel for the uncompleted portion of the trip
    const distance = getDisplayDistance(selectedSystem);
    const warpFactor = selectedWarpFactor || getDefaultWarpFactor();
    const totalFuelNeeded = NavUtils.calculateFuelNeeded(distance, warpFactor, getFuelEfficiencyBonus());
    // Approximate remaining fuel to consume based on progress not yet traveled
    // (fuel has been consumed continuously up to current progress)
    const remainingFuelFraction = 1 - (warpTravelProgressRef.current || 0) / 100;
    const remainingFuel = totalFuelNeeded * remainingFuelFraction;
    if (remainingFuel > 0) removeFuel(remainingFuel);

    // Trigger arrival
    GameAlert.success(`✓ ARRIVED AT ${selectedSystem?.name || 'DESTINATION'} - Warp travel complete`);
    setActiveEvent(null);
    setHasArrived(true);
    setShowArrivalScreen(true);
    setShowWarpEffect(false);
    setWarpDisengaging(false);
    setWarpElements([]);
    setWarpTravelProgress(100);
    SoundEffects.stop('warp_travel');

    setCurrentLocation(() => ({
      ...selectedSystem,
      inTransit: false
    }));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (warpTimerRef.current) {
        clearInterval(warpTimerRef.current);
      }
    };
  }, []);

  return {
    startWarpTimer,
    skipWarp,
    getDisplayDistance,
    generatePlanetStars,
    warpTimerRef
  };
};

export default useWarpTravelLogic;
